import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "antd";
import {
  useMarkBusAsArrivedMutation,
  useCancelBusArrivalMutation,
  useMarkBusNotArrivedHotelsMutation,
} from "../../../services/arrivals";
import { LabelValue } from "@/Component/Value";
import TableC from "@/CommonElements/TableC";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import { Bus01Icon, Cancel01Icon } from "@hugeicons/core-free-icons/index";
import { Button, Stack } from "react-bootstrap";
import BtnAsLink from "@/Component/BtnAsLink";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

export const BusListModal = ({ isOpen, toggle, busCards }) => {
  // =========================
  // hooks
  // =========================
  const { t } = useTranslation();
  const [waitingForFreshData, setWaitingForFreshData] = useState(false);
  const { getLocalizedDate } = useLocalizedText();
  // =========================
  // mutations
  // =========================
  const markBusAsArrivedMutation = useMarkBusAsArrivedMutation();
  const cancelBusArrivalMutation = useCancelBusArrivalMutation();
  const markBusNotArrivedHotelsMutation = useMarkBusNotArrivedHotelsMutation();

  const { cachedBasicData } = useCachedBasicData();
  // Clear waiting state when fresh data arrives (busCards prop changes)
  useEffect(() => {
    if (waitingForFreshData) {
      setWaitingForFreshData(false);
    }
  }, [busCards]);

  // Clear waiting state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setWaitingForFreshData(false);
    }
  }, [isOpen]);

  const handleCompleteArrival = (busCardId) => {
    markBusAsArrivedMutation.mutate(
      {
        busCardId,
      },
      {
        onSuccess: () => {
          // Set waiting state to keep skeleton showing until fresh data arrives
          setWaitingForFreshData(true);
        },
        onError: () => {
          setWaitingForFreshData(false);
        },
      }
    );
  };

  const handleCancelArrival = (busCardId) => {
    markBusNotArrivedHotelsMutation.mutate(
      {
        busCardId,
      },
      {
        onSuccess: () => {
          // Set waiting state to keep skeleton showing until fresh data arrives
          setWaitingForFreshData(true);
        },
        onError: () => {
          setWaitingForFreshData(false);
        },
      }
    );
  };

  // Check if any mutation is pending OR we're waiting for fresh data
  const isAnyMutationPending =
    markBusAsArrivedMutation.isPending ||
    cancelBusArrivalMutation.isPending ||
    markBusNotArrivedHotelsMutation.isPending ||
    waitingForFreshData;

  const getStatusConfig = (status) => {
    if (status === "arrived_hotel") {
      return {
        color: "success",
        text: t("STATUS_ARRIVED"),
      };
    }

    return {
      color: "brand",
      text: t("STATUS_ARRIVING"),
    };
  };

  const columns = [
    {
      key: "bus_number",
      name: t("BUS_NUMBER"),
    },
    {
      key: "capacity",
      name: t("PILGRIMS_COUNT"),
    },
    {
      key: "company_name",
      name: t("TRANSPORT_COMPANY"),
    },
    {
      key: "departure_time",
      name: t("DEPARTURE_TIME"),
    },
    {
      key: "status",
      name: t("STATUS"),
      render: (value) => (
        <LabelValue
          loading={isAnyMutationPending}
          value={value}
          renderValue={() => (
            <Tag className={getStatusConfig(value).color}>
              {getStatusConfig(value).text}
            </Tag>
          )}
        />
      ),
    },
    {
      key: "actions",
      name: t("ACTIONS"),
    },
  ];

  const getActions = (rowData) =>
    [
      rowData.status !== "arrived_hotel" && {
        label: (
          <BtnAsLink
            className="btn-primary btn-xs"
            onClick={() => handleCompleteArrival(rowData.id)}
            disabled={
              markBusAsArrivedMutation.isPending || !rowData.departure_time
            }
            icon={<HugeiconsIcon icon={Bus01Icon} />}
          >
            {t("COMPLETE_ARRIVAL")}
          </BtnAsLink>
        ),
        key: "complete_arrival",
      },
      rowData.status === "arrived_hotel" && {
        label: (
          <BtnAsLink
            className="btn-danger btn-xs"
            onClick={() => handleCancelArrival(rowData.id)}
            disabled={markBusNotArrivedHotelsMutation.isPending}
            icon={<HugeiconsIcon icon={Cancel01Icon} />}
          >
            {t("CANCEL_ARRIVAL")}
          </BtnAsLink>
        ),
        key: "cancel_arrival",
      },
    ].filter(Boolean);

  const busesDetails = busCards.map((busCard) => {
    const bus = cachedBasicData.buses?.find(
      (bus) => String(bus.truck_no) === String(busCard.bus_number)
    );

    return {
      ...bus,
      ...busCard,
    };
  });

  return (
    <>
      <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>
          <span className="fw-bold H5">{t("BUS_LIST_MODAL_TITLE")}</span>
        </ModalHeader>
        <ModalBody>
          {busesDetails && busesDetails.length > 0 ? (
            <TableC
              columnsFilter={false}
              actions={getActions}
              data={busesDetails}
              columns={columns}
            />
          ) : (
            <div className="text-center py-5">
              <HugeiconsIcon size={40} icon={Bus01Icon} />
              <h4 className="text-muted mb-2">{t("NO_BUSES_FOUND")}</h4>
              <p className="text-muted mb-0">
                {t("NO_BUSES_DESCRIPTION") ||
                  "لا توجد حافلات متاحة لهذا الوصول"}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Stack direction="horizontal" gap={2}>
            <Button
              className="btn-danger btn-xs"
              onClick={toggle}
              disabled={
                markBusAsArrivedMutation.isPending ||
                cancelBusArrivalMutation.isPending
              }
            >
              <HugeiconsIcon size={20} icon={Cancel01Icon} />
              {t("CANCEL")}
            </Button>
          </Stack>
        </ModalFooter>
      </Modal>
    </>
  );
};
